import React, { useState, useEffect, useCallback } from 'react';

import { Control } from '@vlabs/uikit';

import { BboxRenderer } from '@vlabs/monokit/shared/ui/canvas';
import type { TBbox, TOriginSize } from '@vlabs/monokit/shared/ui/canvas';

import st from './FileWithDetectionsViewer.module.sass';

export interface TFileDropzoneFile {
  type?: string;
  raw?: File;
  binary?: string | null;
}

interface FileWithDetectionsViewerProps {
  file: TFileDropzoneFile;
  coloredBboxList?: TBbox[];
  onBBoxClick: (b: TBbox[] | null) => void;
}

export function FileWithDetectionsViewer({
  file,
  coloredBboxList = undefined,
  onBBoxClick,
}: FileWithDetectionsViewerProps): React.JSX.Element {
  const [renderedImage, setRenderedImage] = useState<HTMLImageElement | undefined>(undefined);
  const [originSize, setOriginSize] = useState<TOriginSize | undefined>(undefined);

  const handleRenderedImage = useCallback((target: EventTarget | null) => {
    if (!(target instanceof HTMLImageElement)) return;
    setRenderedImage(target);
  }, []);

  useEffect(() => {
    if (!renderedImage) return;
    setOriginSize({
      width: renderedImage.naturalWidth,
      height: renderedImage.naturalHeight,
    });
  }, [renderedImage]);

  const renderFile = () => {
    if (!file) return null;

    switch (file.raw?.type) {
      case 'image/x-portable-pixmap':
        return (
          <Control.PpmFileRenderer
            photo={file.binary}
            setRenderedImage={handleRenderedImage}
          />
        );
      case 'image/tiff':
        return (
          <Control.TiffFileRenderer
            photo={file.binary}
            setRenderedImage={handleRenderedImage}
          />
        );
      case 'image/jpeg':
      case 'image/png':
      case 'image/bmp':
        return (
          <Control.ImageFileRenderer
            photo={file.binary}
            setRenderedImage={handleRenderedImage}
          />
        );
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <div className={st.Renderer}>
        {renderFile()}
      </div>
      <BboxRenderer
        bboxList={coloredBboxList}
        onBBoxClick={onBBoxClick}
        originSize={originSize}
      />
    </React.Fragment>
  );
}
